import { supabase } from "@/integrations/supabase/client";
import { behaviorSummary } from "@/lib/behavior";


const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;
export type UtmKey = (typeof UTM_KEYS)[number];
export type UtmData = Partial<Record<UtmKey, string>> & { landing_page?: string; referrer?: string };

const STORAGE_KEY = "tc_utm";

/**
 * Captures UTM parameters from the current URL (first touch wins) and keeps
 * them in sessionStorage so any lead form on the site can attach the origin.
 */
export function captureUtm(): UtmData {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const fromUrl: UtmData = {};
  UTM_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) fromUrl[k] = v.slice(0, 200);
  });

  const stored = getUtm();
  if (Object.keys(fromUrl).length === 0) return stored;

  const data: UtmData = {
    ...fromUrl,
    landing_page: window.location.pathname,
    referrer: document.referrer || undefined,
  };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
  return data;
}

export function getUtm(): UtmData {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}") as UtmData;
  } catch {
    return {};
  }
}

export function utmSummary(): string {
  const utm = getUtm();
  const parts = [
    utm.utm_source && `utm_source: ${utm.utm_source}`,
    utm.utm_medium && `utm_medium: ${utm.utm_medium}`,
    utm.utm_campaign && `utm_campaign: ${utm.utm_campaign}`,
    utm.utm_content && `utm_content: ${utm.utm_content}`,
    utm.utm_term && `utm_term: ${utm.utm_term}`,
    utm.landing_page && `Página de entrada: ${utm.landing_page}`,
    utm.referrer && `Referrer: ${utm.referrer}`,
  ].filter(Boolean);
  return parts.length ? parts.join(" | ") : "Origem: acesso direto";
}

export interface LeadInput {
  name?: string;
  phone?: string;
  email?: string;
  /** Property / campaign the lead came from. */
  propertyName?: string;
  neighborhood?: string | null;
  /** Extra details appended to the notes. */
  notes?: string[];
  temperature?: "frio" | "morno" | "quente";
  /** ISO date of the visit the lead requested (CRM "Agenda"). */
  visitDate?: string;
}

/** Saves a lead into the CRM (crm_leads), always attaching UTM origin data. */
export async function submitCrmLead(input: LeadInput) {
  const name = (input.name || "").trim();
  const phone = (input.phone || "").trim();
  const email = (input.email || "").trim();

  const notes = [
    ...(input.notes || []).filter(Boolean),
    typeof window !== "undefined" ? `URL: ${window.location.href}` : "",
    utmSummary(),
    typeof window !== "undefined" ? behaviorSummary(window.location.pathname) : "",
  ]
    .filter(Boolean)
    .join(" | ");


  const utm = getUtm();

  return await (supabase as any).from("crm_leads").insert({
    client_name: name || "Lead sem nome",
    client_phone: phone || (email ? "Contato por e-mail" : "Não informado"),
    client_email: email || null,
    property_name:
      [input.propertyName, utm.utm_campaign && `Campanha: ${utm.utm_campaign}`]
        .filter(Boolean)
        .join(" — ")
        .slice(0, 250) || null,
    neighborhood: input.neighborhood || null,
    temperature: input.temperature || "morno",
    status: "novo",
    visit_date: input.visitDate || null,
    general_notes: notes,
  });
}
